import { STORAGE_KEY_B2C_PREFS } from './constants';
import { CF_ID_TO_PROGRAM_LEVEL, PROGRAM_LEVEL_TO_CF_IDS } from './programLevelConstants';

export interface B2cPrefs {
  countries: string[];
  countryCfIds: number[];
  degreeLevel: string;
  degreeLevelCfId: number | null;
  studyAreas: string[];
  studyAreaCfIds: number[];
  city?: string;
  nearestOffice?: string;
  coachingType?: string;
  lookingForCoaching?: 'Yes' | 'No';
  preferredCoaching?: string;
  counsellorName?: string;
  counsellorEmail?: string;
  counsellorPhone?: string;
  counsellorWhatsapp?: string;
  studentModule?: string;
  partnerUuid?: string;
  counsellorUuid?: string;
  nearestOfficeRequired?: boolean;
  _pendingUpdate?: boolean;
}

export function readB2cPrefs(): B2cPrefs | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_B2C_PREFS);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error('[prefs] Failed to parse stored B2C preferences:', error);
    return null;
  }
}

export function writeB2cPrefs(prefs: Partial<B2cPrefs>): void {
  if (typeof window === 'undefined') return;
  const current = readB2cPrefs() || {};
  localStorage.setItem(STORAGE_KEY_B2C_PREFS, JSON.stringify({ ...current, ...prefs }));
}

export function clearB2cPrefs(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY_B2C_PREFS);
}

const hasNonEmptyEntries = (arr?: string[]): boolean =>
  Array.isArray(arr) && arr.length > 0 && arr.every((entry) => !!entry?.trim());

const REQUIRED_PREF_FIELDS: Array<{ label: string; check: (prefs: B2cPrefs) => boolean }> = [
  { label: 'Countries', check: (prefs) => hasNonEmptyEntries(prefs.countries) },
  { label: 'Study Level', check: (prefs) => !!prefs.degreeLevel?.trim() },
  { label: 'Study Areas', check: (prefs) => hasNonEmptyEntries(prefs.studyAreas) },
  { label: 'City', check: (prefs) => !!prefs.city?.trim() },
  { label: 'Nearest Office', check: (prefs) => prefs.nearestOfficeRequired === false || !!prefs.nearestOffice?.trim() },
  { label: 'Partner', check: (prefs) => !!prefs.partnerUuid?.trim() },
];

export function arePrefsComplete(prefs: B2cPrefs | null): boolean {
  if (!prefs) return false;
  return REQUIRED_PREF_FIELDS.every(({ check }) => check(prefs));
}

export function hasVisibleWizardPrefs(prefs: B2cPrefs | null): boolean {
  if (!prefs) return false;
  return Boolean(
    (prefs.countries && prefs.countries.length) ||
    (prefs.degreeLevel && prefs.degreeLevel.trim()) ||
    (prefs.studyAreas && prefs.studyAreas.length)
  );
}

export function getMissingPrefFields(prefs: B2cPrefs | null): string[] {
  if (!prefs) return REQUIRED_PREF_FIELDS.map(({ label }) => label);
  return REQUIRED_PREF_FIELDS.filter(({ check }) => !check(prefs)).map(({ label }) => label);
}

export function buildApiPreferences(prefs: B2cPrefs) {
  return {
    countries: (prefs.countries || []).map((name, index) => ({
      id: prefs.countryCfIds?.[index] ?? 0,
      name,
    })),
    // Expand unified bucket → one {id, name} per CF master id.
    // Legacy fallback: reverse-resolve a stale degreeLevelCfId to its bucket.
    studyLevels: (() => {
      let bucketLabel: string | undefined =
        prefs.degreeLevel && PROGRAM_LEVEL_TO_CF_IDS[prefs.degreeLevel]
          ? prefs.degreeLevel
          : undefined;
      if (!bucketLabel && prefs.degreeLevelCfId != null && CF_ID_TO_PROGRAM_LEVEL[prefs.degreeLevelCfId]) {
        bucketLabel = CF_ID_TO_PROGRAM_LEVEL[prefs.degreeLevelCfId];
      }
      if (!bucketLabel) return [];
      return (PROGRAM_LEVEL_TO_CF_IDS[bucketLabel] || []).map((id) => ({
        id,
        name: bucketLabel as string,
      }));
    })(),
    studyAreas: (prefs.studyAreas || []).map((name, index) => ({
      id: prefs.studyAreaCfIds?.[index] ?? 0,
      name,
    })),
  };
}